import { useRef, useState, useEffect } from "react";
import { SendHorizonal, Radio, Loader2, Volume2, AudioLines, ImagePlus, X, Camera, ImageUp } from "lucide-react";
import { toast } from "sonner";

const MAX_IMAGES = 4;

async function fileToResizedDataUrl(file, maxDim = 1024, quality = 0.72) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    const scale = maxDim / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", quality);
}

export function ChatInput({ value, onChange, onSend, disabled, mode, liveMode, onToggleLive, listening, transcribing, speaking, liveTranscript, wakeWord }) {
  const [images, setImages] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const menuRef = useRef(null);
  const isTech = mode === "technician";

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const doSend = () => {
    if (disabled) return;
    if (!value.trim() && images.length === 0) return;
    onSend(images);
    setImages([]);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      doSend();
    }
  };

  const autoGrow = (e) => {
    onChange(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 180) + "px";
  };

  const pickImages = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    const room = MAX_IMAGES - images.length;
    if (room <= 0) { toast.error(`Up to ${MAX_IMAGES} photos`); return; }
    setProcessing(true);
    try {
      const next = [];
      for (const f of files.slice(0, room)) {
        if (!/^image\/(jpeg|png|webp|jpg|heic|heif)$/i.test(f.type) && !f.type.startsWith("image/")) continue;
        next.push(await fileToResizedDataUrl(f));
      }
      setImages((prev) => [...prev, ...next]);
    } catch {
      toast.error("Couldn't read that photo");
    } finally {
      setProcessing(false);
    }
  };

  const accent = mode === "technician" ? "var(--tech-accent)" : "var(--primary)";

  let placeholder;
  if (liveMode) {
    if (transcribing) placeholder = "Transcribing…";
    else if (speaking) placeholder = "Apex is speaking…";
    else if (listening) placeholder = "Listening… just speak";
    else placeholder = "Live voice on";
  } else {
    placeholder = isTech ? "Describe the issue…" : "Ask Apex anything…";
  }

  let statusLine = "Apex can make mistakes. Verify critical technical actions before executing.";
  if (liveMode) {
    if (transcribing) statusLine = "Got it — processing what you said…";
    else if (speaking) statusLine = "Apex is replying… it'll listen again automatically";
    else if (listening) statusLine = "Live voice is on — just talk, no buttons. Tap the live button to stop.";
    else statusLine = "Live voice on — waiting for the bot to finish.";
  } else if (isTech) {
    statusLine = "Tech Co-Pilot — attach photos of screens, hardware or cabling for visual troubleshooting.";
  } else if (wakeWord) {
    statusLine = "Wake word on — say \u201cHey Apex\u201d to start talking hands-free.";
  }

  return (
    <div className="px-4 md:px-6 pb-4 pt-2 shrink-0">
      <div className="mx-auto max-w-3xl">
        {images.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2" data-testid="image-preview-strip">
            {images.map((src, i) => (
              <div key={i} className="relative h-16 w-16 rounded-lg overflow-hidden border border-[var(--border)]" data-testid="image-preview">
                <img src={src} alt="attachment" className="h-full w-full object-cover" />
                <button
                  data-testid="image-remove"
                  onClick={() => setImages((prev) => prev.filter((_, idx) => idx !== i))}
                  className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/70 grid place-items-center text-white hover:bg-[var(--alert)]"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
        {liveMode && liveTranscript && (
          <div
            data-testid="live-transcript"
            className="mb-2 rounded-xl border border-[var(--primary)]/40 bg-[var(--primary)]/5 px-4 py-2.5 text-[15px] text-[var(--text-primary)]"
          >
            <span className="mr-2 text-[10px] font-mono-tech uppercase tracking-widest text-[var(--primary)]">You</span>
            {liveTranscript}
            <span className="ml-1 inline-block h-4 w-0.5 align-middle bg-[var(--primary)] pulse-dot" />
          </div>
        )}
        <div
          className={`flex items-end gap-2 rounded-2xl border bg-[var(--surface)] p-2 transition-colors duration-200 ${
            liveMode ? "border-[var(--primary)]" : "border-[var(--border)] focus-within:border-[var(--primary)]"
          }`}
          style={{ boxShadow: "0 8px 30px rgba(0,0,0,0.35)" }}
        >
          <button
            data-testid="live-voice-button"
            onClick={onToggleLive}
            title={liveMode ? "Stop live voice" : "Start hands-free live voice"}
            className={`shrink-0 h-10 w-10 rounded-xl grid place-items-center transition-colors duration-200 ${
              liveMode ? "bg-[var(--primary)] text-black" : "text-[var(--text-secondary)] hover:text-white hover:bg-white/5"
            }`}
          >
            {liveMode && transcribing ? <Loader2 size={18} className="animate-spin" />
              : liveMode && speaking ? <Volume2 size={18} className="pulse-dot" />
              : liveMode && listening ? <AudioLines size={18} className="pulse-dot" />
              : <Radio size={18} />}
          </button>

          <div className="relative shrink-0" ref={menuRef}>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={pickImages} className="hidden" data-testid="image-camera-input" />
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={pickImages} className="hidden" data-testid="image-file-input" />
            <button
              data-testid="image-attach-button"
              onClick={() => setMenuOpen((o) => !o)}
              disabled={processing || images.length >= MAX_IMAGES}
              title={isTech ? "Add a photo to troubleshoot" : "Add a photo for your warranty claim"}
              className="h-10 w-10 rounded-xl grid place-items-center hover:bg-white/5 transition-colors disabled:opacity-40"
              style={{ color: accent }}
            >
              {processing ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
            </button>
            {menuOpen && (
              <div
                data-testid="image-source-menu"
                className="absolute bottom-12 left-0 z-20 w-48 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-1.5 shadow-2xl"
              >
                <button
                  data-testid="image-take-photo-option"
                  onClick={() => { setMenuOpen(false); cameraRef.current?.click(); }}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-[var(--text-primary)] hover:bg-white/5 transition-colors"
                >
                  <Camera size={16} style={{ color: accent }} />
                  Take Photo
                </button>
                <button
                  data-testid="image-upload-gallery-option"
                  onClick={() => { setMenuOpen(false); fileRef.current?.click(); }}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-[var(--text-primary)] hover:bg-white/5 transition-colors"
                >
                  <ImageUp size={16} style={{ color: accent }} />
                  Upload from Gallery
                </button>
              </div>
            )}
          </div>

          <textarea
            data-testid="chat-input-textarea"
            value={value}
            onChange={autoGrow}
            onKeyDown={handleKey}
            rows={1}
            placeholder={placeholder}
            className="flex-1 resize-none bg-transparent px-2 py-2 text-[15px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none max-h-[180px]"
          />
          <button
            data-testid="send-message-button"
            onClick={doSend}
            disabled={disabled || (!value.trim() && images.length === 0)}
            className="shrink-0 h-10 w-10 rounded-xl grid place-items-center text-black font-semibold transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:brightness-110"
            style={{ background: accent }}
          >
            <SendHorizonal size={18} />
          </button>
        </div>
        <p className={`mt-2 text-center text-[11px] font-mono-tech ${liveMode ? "text-[var(--primary)]" : "text-[var(--text-muted)]"}`} data-testid="input-status-line">
          {statusLine}
        </p>
      </div>
    </div>
  );
}
